import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { apiFetch } from "../utils/apifetch";
import "./Journal.css";

const SEARCH_DEBOUNCE_MS = 300;

const emptyForm = {
  id: null,
  shortName: "",
  longName: "",
  activeStatus: "active",
};

export default function Journal() {
  const navigate = useNavigate();
  const { logout, hasPermission } = useAuth();

  const [journals, setJournals] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");

  /* ===== TOAST ===== */
  const [showToast, setShowToast] = useState(false);

  /* ===== SEARCH ===== */
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  /* ===== SORT ===== */
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  /* ===== FORM ===== */
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const isEdit = !!form.id;

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  /* ===== DEBOUNCE ===== */
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [searchInput]);

  /* ===== LOAD DATA ===== */

  const loadJournals = async () => {
    try {
      const res = await apiFetch(
        `/journal`,
        { headers: authHeader() },
        logout,
        navigate
      );

      setJournals(await res.json());
    } catch (err) {
      setError(err.message || "Unable to load journals");
    }
  };

  const loadSummary = async () => {
    try {
      const res = await apiFetch(
        `/journal/summary`,
        { headers: authHeader() },
        logout,
        navigate
      );

      if (res.ok) setSummary(await res.json());
    } catch {}
  };

  useEffect(() => {
    Promise.all([loadJournals(), loadSummary()]).finally(() =>
      setLoading(false)
    );
  }, []);

  /* ===== SORT ===== */

  const handleSort = (key) => {
    setSortDir((prev) =>
      sortKey === key ? (prev === "asc" ? "desc" : "asc") : "asc"
    );
    setSortKey(key);
  };

  /* ===== FILTER + SORT ===== */

  const filteredJournals = useMemo(() => {
    const q = search.toLowerCase();

    let data = journals.filter(
      (j) =>
        j.shortName?.toLowerCase().includes(q) ||
        j.longName?.toLowerCase().includes(q) ||
        j.activeStatus?.toLowerCase().includes(q)
    );

    if (sortKey) {
      data = [...data].sort((a, b) => {
        const x = (a[sortKey] ?? "").toString().toLowerCase();
        const y = (b[sortKey] ?? "").toString().toLowerCase();

        if (x < y) return sortDir === "asc" ? -1 : 1;
        if (x > y) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [journals, search, sortKey, sortDir]);

  /* ===== FORM HANDLERS ===== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddJournal = () => {
    setForm({ ...emptyForm });
    setFormError("");
    setShowForm(true);
  };

  const handleEditJournal = (j) => {
    setForm({
      id: j.id,
      shortName: j.shortName,
      longName: j.longName,
      activeStatus: j.activeStatus,
    });

    setFormError("");
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setFormError("");
    setShowForm(false);
  };

  const submit = async () => {
    const method = isEdit ? "PUT" : "POST";
    const url = isEdit ? `/journal/${form.id}` : `/journal`;

    const payload = {
      shortName: form.shortName,
      longName: form.longName,
      activeStatus: form.activeStatus,
      createdByUid: localStorage.getItem("uid"),
    };

    try {
      const res = await apiFetch(
        url,
        {
          method,
          headers: authHeader(),
          body: JSON.stringify(payload),
        },
        logout,
        navigate
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      resetForm();
      loadJournals();
      loadSummary();

      /* ===== TOAST ONLY FOR ADD ===== */
      if (!isEdit) {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }
    } catch (err) {
      setFormError(err.message || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this journal?")) return;

    await apiFetch(
      `/journal/${id}`,
      { method: "DELETE", headers: authHeader() },
      logout,
      navigate
    );

    loadJournals();
    loadSummary();
  };

  /* ===== UI ===== */

  return (
    <div className="journal-page">
      <h2>List of Journals</h2>

      <div className="journal-cards">
        <div className="journal-card total">
          Total Journals <span>{summary.total}</span>
        </div>

        <div className="journal-card active">
          Active Journals <span>{summary.active}</span>
        </div>

        <div className="journal-card inactive">
          Inactive Journals <span>{summary.inactive}</span>
        </div>
      </div>

      <div className="journal-filters">
        <input
          placeholder="Search by Journal Short Name & Journal Full Name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <button
          className="add-journal-btn"
          onClick={handleAddJournal}
          disabled={!hasPermission("journal", "create")}
        >
          + Add Journal
        </button>
      </div>

      {showForm && (
        <div className="journal-inline-form">
          <input
            name="shortName"
            placeholder="Journal Short Name"
            value={form.shortName}
            onChange={handleChange}
          />

          <input
            name="longName"
            placeholder="Journal Full Name"
            value={form.longName}
            onChange={handleChange}
          />

          <select
            name="activeStatus"
            value={form.activeStatus}
            onChange={handleChange}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button className="save-btn" onClick={submit}>
            Save
          </button>

          <button className="cancel-btn" onClick={resetForm}>
            Cancel
          </button>
        </div>
      )}

      {!loading && (
        <table className="journal-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("shortName")}>
                Journal Short Name <span className="sort-icons">▲▼</span>
              </th>

              <th onClick={() => handleSort("longName")}>
                Journal Full Name <span className="sort-icons">▲▼</span>
              </th>

              <th onClick={() => handleSort("activeStatus")}>
                Status <span className="sort-icons">▲▼</span>
              </th>

              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredJournals.map((j) => (
              <tr key={j.id}>
                <td>{j.shortName}</td>

                <td>{j.longName}</td>

                <td>
                  <span className={`status ${j.activeStatus}`}>
                    {j.activeStatus}
                  </span>
                </td>

                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => handleEditJournal(j)}
                      disabled={!hasPermission("journal", "update")}
                    >
                      Edit
                    </button>

                    <button
                      className="delete-btn"
                      onClick={() => del(j.id)}
                      disabled={!hasPermission("journal", "delete")}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {!filteredJournals.length && (
              <tr>
                <td colSpan="4" className="no-data">
                  No Journals found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {error && <p className="form-error">{error}</p>}
      {formError && <p className="form-error">{formError}</p>}

      {showToast && (
        <div className="toast-center-success">
          Journal successfully added
        </div>
      )}
    </div>
  );
}
